import type { AvailabilityCutoffs, CellState } from "@/lib/availability/cell-state";
import { cn } from "@/lib/utils";
import { IssueCorner } from "./Chips";
import { CELL_CLASS } from "./cell-style";
import { STATE_LABEL } from "./labels";

interface LegendItem {
  state: CellState;
  sample: string;
  rule: string;
}

function legendItems(cutoffs: AvailabilityCutoffs): LegendItem[] {
  return [
    { state: "overUtilized", sample: "+42", rule: `${cutoffs.overThresholdPct}%+` },
    {
      state: "stretched",
      sample: "+12",
      rule: `${cutoffs.stretchedThresholdPct}–${cutoffs.overThresholdPct}%`,
    },
    {
      state: "fullyBooked",
      sample: "",
      rule: `${cutoffs.fullyBookedFloorPct}–${cutoffs.stretchedThresholdPct}%, no number`,
    },
    {
      state: "someRoom",
      sample: "28",
      rule: `${cutoffs.someRoomFloorPct}–${cutoffs.fullyBookedFloorPct}%`,
    },
    { state: "available", sample: "64", rule: `under ${cutoffs.someRoomFloorPct}%` },
    { state: "unplanned", sample: "", rule: "past plan horizon" },
    { state: "noData", sample: "–", rule: "" },
  ];
}

const SAMPLE_BASE =
  "relative inline-flex h-5 w-[34px] items-center justify-center rounded-[3px] font-mono text-xs";

interface AvailabilityLegendProps {
  cutoffs: AvailabilityCutoffs;
  /** The active tile filter's chip text ("Showing over-utilized"), or null. */
  activeFilter: string | null;
  onClearFilter: () => void;
}

/** The seven states and the two modifiers, with the live cut-offs; plus the "Clear" chip. */
export function AvailabilityLegend({
  cutoffs,
  activeFilter,
  onClearFilter,
}: AvailabilityLegendProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2.5">
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground">
        {legendItems(cutoffs).map((item) => (
          <li key={item.state} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn(SAMPLE_BASE, CELL_CLASS[item.state])}>
              {item.sample}
            </span>
            <span className="font-semibold text-heading">{STATE_LABEL[item.state]}</span>
            {item.rule ? <span className="text-muted-foreground">{item.rule}</span> : null}
          </li>
        ))}
        <li className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn(SAMPLE_BASE, "avail-hatch border bg-card text-heading")}
          />
          <span className="font-semibold text-heading">Proposed</span>
          <span className="text-muted-foreground">hatched</span>
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span aria-hidden className={cn(SAMPLE_BASE, "border bg-card text-heading")}>
            <IssueCorner className="size-2" />
          </span>
          <span className="font-semibold text-heading">Issue</span>
          <span className="text-muted-foreground">check data</span>
        </li>
      </ul>
      {activeFilter !== null ? (
        <button
          type="button"
          onClick={onClearFilter}
          className="h-8 cursor-pointer rounded-full border border-primary bg-accent px-3 text-xs font-semibold text-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {activeFilter} · Clear
        </button>
      ) : null}
    </div>
  );
}
